import React from 'react';
import { connect } from 'react-redux';

import { createComponent, enums, Header, Label, Layout, PasswordField, ProgressBar, Button, Input } from 'console-kit';

import ResetPasswordPanelSelectors from '../redux/selectors';
import ResetPasswordPanelActions from '../redux/actions';

import authStatics from '../../../common/static';
import authEnums from '../../../common/enums';

import { pwdStengthEvaluation, rePasswordValidationHelper } from '../../../common/utils';
import AuthImageLogo from '../../../components/AuthImageLogo';

const ResetPasswordPanel = createComponent({
  displayName: 'ResetPasswordPanel',
  refs:        'resetPasswordFormRef',
  state:       {
    passwordValidation: {
      value: {
        rePasswordCorrect:      true,
        rePasswordErrorMessage: undefined,

        passwordErrorMessage: undefined,
        passwordCorrect:      true,
      },
      updater: 'setPasswordValidation',
    },
  },
  handlers: {
    handlePasswordFieldBlur: props => () => {
      const _minPwdLength = props.isSupportUser ?
        authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
        authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      props.setPasswordValidation({
        ...props.passwordValidation,
        passwordErrorMessage: isPasswordValid ? '' : 'Invalid Password',
        passwordCorrect:      isPasswordValid,
      });
    },

    handleRePasswordFieldBlur: props => () => {
      const _minPwdLength = props.isSupportUser ?
        authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
        authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      const { rePasswordErrorMessage, rePasswordCorrect } = rePasswordValidationHelper(
        props.password,
        props.repassword,
        isPasswordValid
      );

      props.setPasswordValidation({
        ...props.passwordValidation,
        rePasswordCorrect:      rePasswordCorrect,
        rePasswordErrorMessage: rePasswordErrorMessage,
      });
    },

    handleValueChange: p => (e, { id, value }) => {
      p.onUpdateField({ fieldId: id, value });
    },

    handleClick: props => (e, { value }) => {
      e.preventDefault();

      const _minPwdLength = props.isSupportUser ?
        authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
        authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      const { rePasswordErrorMessage, rePasswordCorrect } = rePasswordValidationHelper(
        props.password,
        props.repassword,
        isPasswordValid
      );

      props.setPasswordValidation({
        rePasswordCorrect:      rePasswordCorrect,
        rePasswordErrorMessage: rePasswordErrorMessage,
        passwordErrorMessage:   isPasswordValid ? '' : 'Invalid Password',
        passwordCorrect:        isPasswordValid,
      });

      props.onResetPasswordClicked(props.refs.resetPasswordFormRef, isPasswordValid && rePasswordCorrect);
    },
  },
  render(props) {
    const _minPwdLength = props.isSupportUser ?
      authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
      authEnums.pwdStrengthLimits.MIN_LENGTH;

    const { progressBarLabel, progressBarRank, progressBarColor, isPasswordValid } = pwdStengthEvaluation(
      props.password,
      _minPwdLength
    );

    return (
      <div className={authStatics.SCREEN_CLASSES_WIDE}>
        <form ref={props.setResetPasswordFormRef} action={authStatics.RESETPASSWORD_FORM_SUBMIT_URL}>
          {/*user for user selection in the BE activation process*/}
          <input type="hidden" value={props.userId} name="id" />

          <Layout>
            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.CENTER}>
                <AuthImageLogo />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.LEFT}>
                <Header
                  text="Set a new password"
                  isDividing={false}
                  font={{
                    emphasis: enums.font.emphasis.NORMAL,
                  }}
                  size={enums.sizes.X6}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.LEFT}>
                <Label
                  text={`Your password must be at least ${_minPwdLength} characters long. For security reasons, we recommend to use a mixture of letters, numbers and special characters. Only good or strong passwords will be accepted.`}
                  hasPadding={false}
                  display={enums.display.PLAIN}
                  size={enums.sizes.X4}
                  isFluid
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.LEFT}>
                <Input label="" isFluid={true} isDisabled={true} value={props.userEmail} />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <PasswordField
                  isFluid={true}
                  label="New Password"
                  hasError={!props.passwordValidation.passwordCorrect}
                  errorMessage={props.passwordValidation.passwordErrorMessage}
                  placeholder=""
                  id="password"
                  name="password"
                  isUnderlined={false}
                  onChange={props.handleValueChange}
                  onBlur={props.handlePasswordFieldBlur}
                  value={props.password}
                  revealTooltipTextDisabled={authStatics.pwdToggleTooltip}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <PasswordField
                  label="Confirm Password"
                  hasError={!props.passwordValidation.rePasswordCorrect}
                  errorMessage={props.passwordValidation.rePasswordErrorMessage}
                  isFluid={true}
                  placeholder=""
                  id="repassword"
                  name="repassword"
                  isUnderlined={false}
                  onChange={props.handleValueChange}
                  onBlur={props.handleRePasswordFieldBlur}
                  value={props.repassword}
                  revealTooltipTextDisabled={authStatics.pwdToggleTooltip}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <ProgressBar
                percentComplete={progressBarRank}
                size={enums.sizes.MEDIUM}
                color={progressBarColor}
                label={progressBarLabel}
              />
            </Layout.Row>

            <Layout.Row>
              <Button.WithAnimation
                actions={{
                  start:   ResetPasswordPanelActions.onResetPasswordRequest,
                  success: ResetPasswordPanelActions.onResetPasswordSuccess, //no action
                  failure: ResetPasswordPanelActions.onResetPasswordFailure, //no action
                }}
              >
                <Button.Primary
                  text="Save"
                  float={enums.positions.RIGHT}
                  size={enums.sizes.X4}
                  onClick={props.handleClick}
                  textAlign={enums.textAlignments.CENTER}
                  color={enums.colors.ACCENT}
                  isSolid
                  isFluid
                />
              </Button.WithAnimation>
            </Layout.Row>
          </Layout>
        </form>
      </div>
    );
  },
});

export default connect(
  state => {
    return {
      userEmail:     ResetPasswordPanelSelectors.userEmail(state),
      userId:        ResetPasswordPanelSelectors.userId(state),
      isSupportUser: ResetPasswordPanelSelectors.isSupportUser(state),

      password:   ResetPasswordPanelSelectors.password(state),
      repassword: ResetPasswordPanelSelectors.repassword(state),
    };
  },
  dispatch => ({
    onUpdateField({ fieldId, value }) {
      dispatch(ResetPasswordPanelActions.updateField(fieldId, value));
    },

    onResetPasswordClicked(formRef, isValid) {
      dispatch(ResetPasswordPanelActions.onResetPasswordRequest(formRef, isValid));
    },
  })
)(ResetPasswordPanel);
